// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@eisinc/App/Components/Notify";
import { anchorId } from "@eisinc/App/Components/Product/ProductOptionsTableLink";
import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import { ProductDetailsVariantOptionsContext } from "@eisinc/App/Contexts/ProductDetailsVariantOptionsContext";
import { createHorizontalSmallHeadingAndTextStyles } from "@eisinc/App/SharedStyles/smallHeadingAndTextStyles";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { getSettingsCollection } from "@eisinc/App/Store/Selectors/SettingsSelectors";
import { parseBoolean } from "@eisinc/App/Utilities/helpers";
import ProductOptionContext from "@eisinc/App/Widgets/ProductDetails/ProductOptionContext";
import Accordion from "@eisinc/MobiusOverrides/Accordion";
import ManagedAccordionSection from "@eisinc/MobiusOverrides/AccordionSection";
import { AccordionSectionPresentationProps } from "@eisinc/MobiusOverrides/AccordionSection/AccordionSection";
import Button from "@eisinc/MobiusOverrides/Button";
import DataTable from "@eisinc/MobiusOverrides/DataTable/DataTable";
import LazyImage, { LazyImageProps } from "@eisinc/MobiusOverrides/LazyImage";
import TextField, { TextFieldPresentationProps } from "@eisinc/MobiusOverrides/TextField";
import Tooltip, { TooltipPresentationProps } from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import ProductAddToCartButton from "@eisinc/Overrides/Components/ProductAddToCartButton";
import { ProductAvailabilityStyles } from "@eisinc/Overrides/Components/ProductAvailability";
import ProductPrice, { ProductPriceStyles } from "@eisinc/Overrides/Components/ProductPrice";
import ProductQuantityOrdered from "@eisinc/Overrides/Components/ProductQuantityOrdered";
import LinkInfo from "@eisinc/XDL/Icons/LinkInfo";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { xScreen } from "@eisinc/XDL/Utilities";
import color from "@eisinc/XDL/Utilities/color";
import printStyles from "@eisinc/XDL/Utilities/print";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import updateVariantSelection from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/UpdateVariantSelection";
import translate from "@insite/client-framework/Translate";
import {
    ChildTraitValueModel,
    ProductInventoryDto,
    ProductModel,
    VariantTraitModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Search from "@insite/mobius/Icons/Search";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select from "@insite/mobius/Select";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cloneDeep from "lodash/cloneDeep";
import * as React from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface VariantChildContext {
    product: ProductModel;
    productInfo: ProductInfo;
}

interface ProductOptionSorting {
    type: "traitId";
    asc: boolean;
    data: string;
}

interface ProductOptionPagination {
    totalItemCount: number;
    page: number;
    pageSize: number;
    filteredChildren: VariantChildContext[];
    visibleChildren: VariantChildContext[];
    oneImageAvailable: boolean;
    sort: ProductOptionSorting;
}

const compareStrings = (stringA: string | undefined, stringB: string | undefined, asc: boolean) => {
    let result = 0;

    if (stringA && stringB) {
        if (asc) {
            result = stringA.localeCompare(stringB);
        } else {
            result = stringB.localeCompare(stringA);
        }
    }

    return result;
};

type SortingMethod = (items: VariantChildContext[], asc: boolean, extraData?: string) => VariantChildContext[];

const sortingMethods: Dictionary<SortingMethod> = {
    productNumber: (items: VariantChildContext[], asc: boolean): VariantChildContext[] => {
        return items.sort((a: VariantChildContext, b: VariantChildContext) =>
            compareStrings(a.product.productNumber, b.product.productNumber, asc),
        );
    },
    traitId: (items: VariantChildContext[], asc: boolean, traitId?: string): VariantChildContext[] => {
        return items.sort((a: VariantChildContext, b: VariantChildContext) => {
            const valueA = a.product.childTraitValues?.find(value => value.styleTraitId === traitId);
            const valueB = b.product.childTraitValues?.find(value => value.styleTraitId === traitId);

            return compareStrings(valueA?.valueDisplay, valueB?.valueDisplay, asc);
        });
    },
};

const priceTextStyle: InjectableCss = {
    css: css`
        &&& {
            font-size: ${fontConfig.singleLineSmall.size}px;
            font-weight: ${fontConfig.weight.medium};
            line-height: ${fontConfig.singleLineSmall.lineHeight};
            margin-bottom: 8px;
        }
    `,
};

const tableRowStyle = css`
    cursor: pointer;
    height: 68px;

    ${xScreen("lte", "md")} {
        height: auto;
    }
`;

export interface ProductDetailsVariantOptionsStyles {
    filtersWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    buttonWrapper?: InjectableCss;
    searchInput?: TextFieldPresentationProps;
    tableWrapper?: InjectableCss;
    tableHeaderCell?: DataTableHeaderPresentationProps;
    tableRow?: DataTableRowProps;
    selectedTableRow?: DataTableRowProps;
    actionsCell?: InjectableCss;
    imageCell?: DataTableCellProps;
    image?: LazyImageProps;
    availabilityExtendedStyles?: ProductAvailabilityStyles;
    priceExtendedStyles?: ProductPriceStyles;
    productActionsWrapper?: InjectableCss;
    tooltipIcon?: IconPresentationProps;
    tooltipTextWrapper?: InjectableCss;
    searchWrapper?: InjectableCss;
    divider?: InjectableCss;
    clearAll?: LinkPresentationProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    accordion?: AccordionSectionPresentationProps;
    actionCellWrapper?: DataTableCellProps;
    tooltip?: TooltipPresentationProps;
}

const productOptionsStyles: ProductDetailsVariantOptionsStyles = {
    clearAll: {
        css: css`
            display: flex;
        `,
    },
    divider: {
        css: css`
            width: 100%;
            height: 1px;
            background: ${color("common.ui3")};
            margin-block: 16px 12px;
        `,
    },
    filtersWrapper: {
        css: css`
            display: grid;
            grid-template-columns: repeat(6, 1fr);
            gap: 16px 22px;
            align-items: center;
            margin-bottom: 12px;
            flex-wrap: wrap;
            ${xScreen("lte", "md")} {
                grid-template-columns: repeat(3, 1fr);
            }
            ${xScreen("lte", "sm")} {
                grid-template-columns: repeat(2, 1fr);
            }
        `,
    },
    searchWrapper: {
        css: css`
            ${xScreen("lte", "sm")} {
                grid-columns: 1/3;
            }
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    tableWrapper: {
        css: css`
            margin-bottom: 16px;
        `,
    },
    headingAndTextStyles: {
        ...createHorizontalSmallHeadingAndTextStyles(),
    },
    tableRow: {
        css: tableRowStyle,
    },
    selectedTableRow: {
        css: css`
            border: 3px solid ${color("primary")};
            ${tableRowStyle}
            @media print {
                &&& {
                    border-bottom: 3px solid ${color("primary")};
                }
            }
        `,
    },
    imageCell: {
        css: css`
            width: 68px;
            max-width: 68px;
            padding: 8px;
            ${xScreen("lte", "md")} {
                width: auto;
                max-width: unset;
            }
        `,
    },
    image: {
        width: "52px",
        height: "52px",
        css: css`
            ${xScreen("lte", "md")} {
                margin-right: auto;
            }
        `,
    },
    availabilityExtendedStyles: {
        messageText: {
            css: css`
                &&& {
                    font-size: ${fontConfig.singleLinexSmall.size}px;
                    line-height: ${fontConfig.singleLinexSmall.lineHeight};
                }
            `,
        },
        availabilityByWarehouseLink: {
            css: css`
                display: none;
            `,
        },
        iconStyles: {
            size: 13,
            css: css`
                margin-right: 4px;
                &,
                svg {
                    width: 12px;
                }
            `,
        },
        container: {
            css: css`
                ${xScreen("lte", "md")} {
                    margin-right: auto;
                }
            `,
        },
        containerWithAdditionalMessage: {
            css: css`
                ${xScreen("lte", "md")} {
                    margin-right: auto;
                }
            `,
        },
    },
    actionCellWrapper: {
        css: css`
            width: 176px;
            ${xScreen("lte", "md")} {
                width: auto;
            }
        `,
    },
    actionsCell: {
        css: css`
            ${xScreen("lte", "md")} {
                display: flex;
                flex-direction: column;
                width: 100%;
            }
        `,
    },
    priceExtendedStyles: {
        price: {
            priceText: priceTextStyle,
            realTimeText: priceTextStyle,
            unitOfMeasureText: {
                ...fontConfig.singleLinexxSmall,
            },
        },
        packWrapper: {
            css: css`
                display: none;
            `,
        },
        priceWrapper: {
            css: css`
                align-items: baseline;
            `,
        },
        quoteMessageWrapper: {
            css: css`
                align-items: baseline;
            `,
        },
        quoteMessage: { text: priceTextStyle },
        wrapper: {
            css: css`
                ${xScreen("lte", "md")} {
                    width: auto;
                }
            `,
        },
    },
    productActionsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            column-gap: 4px;
            ${printStyles.hideOnPrint}
        `,
    },
    tooltip: {
        cssOverrides: {
            tooltipClickable: css`
                display: flex;
                margin-left: 4px;
            `,
        },
    },
    tooltipIcon: {
        size: 14,
        color: "primary",
        css: css`
            &,
            svg {
                height: 13px;
            }
            &:hover {
                color: ${color("common.ui8")};
            }
        `,
    },
    tooltipTextWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
        `,
    },
    searchInput: {
        iconProps: {
            size: 16,
            css: css`
                top: 1px;
                right: 1px;
                width: 32px;
                height: 32px;
            `,
        },
    },
    accordion: {
        mergeCss: true,
        panelProps: {
            css: css`
                padding-bottom: 0;
            `,
        },
    },
    tableHeaderCell: {
        css: css`
            &&& {
                border-left: 0;
                border-right: 0;
            }
        `,
    },
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasParentProductId;

const mapStateToProps = (state: ApplicationState, ownProps: HasParentProductId) => {
    const variantChildrenDataView = getVariantChildrenDataView(state, ownProps.parentProductId);
    const variantChildren = variantChildrenDataView.value;

    const variantChildContexts: VariantChildContext[] = (variantChildren ?? []).map(product => ({
        product,
        productInfo: createFromProduct(product),
    }));

    const product = getProductState(state, ownProps.parentProductId);
    const variantTraits = product.value?.variantTraits;
    const settings = getSettingsCollection(state);
    const defaultSize = (settings as any).customSettings?.defaultChildItemsToShow;

    const isVariantOptionsLoading = product.isLoading || variantChildrenDataView.isLoading || !variantTraits;

    return {
        variantTraits,
        variantChildContexts,
        isVariantOptionsLoading,
        selectedVariantId: state.pages.productDetails.selectedProductId,
        showInventoryAvailability: settings.productSettings.showInventoryAvailability,
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        pageSize: Number(defaultSize) || variantChildContexts.length,
        enableRealTimeBomFabricationCheck: parseBoolean(settings.customSettings?.enableRealTimeBomFabricationCheck),
    };
};

const mapDispatchToProps = {
    loadRealTimePricing,
    loadRealTimeInventory,
    updateVariantSelection,
};

const styles = productOptionsStyles;

const sortTraitValuesOfVisibleChildren = (
    variantTraits: VariantTraitModel[],
    visibleContext: VariantChildContext[],
) => {
    visibleContext.every(context => {
        const sortedChildValues: ChildTraitValueModel[] = [];

        variantTraits.every(trait => {
            const value = context.product.childTraitValues?.find(value => trait.id === value.styleTraitId);
            value && sortedChildValues.push(value);
            return true;
        });

        context.product = { ...context.product, childTraitValues: sortedChildValues };

        return true;
    });
};

const hasOneAvailableImage = (visibleChildren: VariantChildContext[]) => {
    let result = false;

    visibleChildren.every(childContext => {
        const images = childContext.product.images;

        if (images && images.length > 0) {
            result = true;
            return false;
        }

        return true;
    });

    return result;
};

const ProductOptions: React.FC<Props> = ({
    variantChildContexts,
    variantTraits,
    isVariantOptionsLoading,
    selectedVariantId,
    showInventoryAvailability,
    loadRealTimePricing,
    loadRealTimeInventory,
    isTabletScreen,
    updateVariantSelection,
    parentProductId,
    pageSize,
    enableRealTimeBomFabricationCheck,
}) => {
    const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
    const [filterQuery, setFilterQuery] = useState("");

    const productDetailsVariantOptionsContext = useContext(ProductDetailsVariantOptionsContext);
    const [pageChanged, setPageChanged] = useState(new Date().getTime());
    const [pagination, setPagination] = useState<ProductOptionPagination>({
        totalItemCount: variantChildContexts.length,
        page: 1,
        pageSize,
        filteredChildren: variantChildContexts,
        visibleChildren: variantChildContexts.slice(0, pageSize),
        oneImageAvailable: hasOneAvailableImage(variantChildContexts.slice(0, pageSize)),
        sort: {
            type: "traitId",
            asc: false,
            data: "",
        },
    });

    const stableChildContextKey = variantChildContexts.map(v => v.product.id).join(",");
    const stableVariantChildContexts = useMemo(() => variantChildContexts, [stableChildContextKey]);

    // update selectedOptions object
    const handleChange = (fieldKey: string, selected: string) => {
        setSelectedOptions(prev => {
            if (selected) {
                return { ...prev, [fieldKey]: selected };
            }

            const { [fieldKey]: _, ...rest } = prev; // destructure to remove key
            return rest;
        });
    };

    // Recalculate the filter whenever the query or variant dropdown changes
    useEffect(() => {
        let items = sortingMethods[pagination.sort.type](
            variantChildContexts,
            pagination.sort.asc,
            pagination.sort.data,
        );

        if (filterQuery) {
            items = items.filter(
                childContext =>
                    childContext.product.productNumber.toLowerCase().indexOf(filterQuery.toLowerCase()) > -1,
            );
        }

        const filteredChildren = items.filter(childContext => {
            const childTraits = childContext.product.childTraitValues ?? [];

            // Check if all selected options match this child’s traits
            return Object.entries(selectedOptions).every(([traitId, selectedValue]) =>
                childTraits.some(traitValue => traitValue.styleTraitId === traitId && traitValue.id === selectedValue),
            );
        });

        setPagination(pagination => {
            const tempPagination = {
                ...pagination,
            };

            tempPagination.filteredChildren = filteredChildren;
            tempPagination.totalItemCount = tempPagination.filteredChildren.length;
            tempPagination.visibleChildren =
                tempPagination.page > 1
                    ? tempPagination.filteredChildren
                    : tempPagination.filteredChildren.slice(0, pageSize);
            tempPagination.oneImageAvailable = hasOneAvailableImage(tempPagination.visibleChildren);

            return tempPagination;
        });

        setPageChanged(new Date().getTime());

        // Select the row if there’s only one result
        if (filteredChildren.length === 1) {
            const variantValues = [] as any;
            const variantIds = [] as any;

            filteredChildren[0].product.childTraitValues?.forEach(variant => {
                variantValues.push(variant.id);
                variantIds.push(variant.styleTraitId);
            });
            setVariantValues(variantValues);
            setVariantIds(variantIds);
        } else {
            // Reset variant values and ids in context if there are multiple or no results
            setVariantValues([]);
            setVariantIds([]);
        }
    }, [selectedOptions, filterQuery, stableVariantChildContexts]);

    const filteredVariantTraits = useMemo(() => {
        // Filter the variant children based on selected options
        const filteredChildren = variantChildContexts.filter(childContext => {
            const childTraits = childContext.product.childTraitValues ?? [];

            // Check if all selected options match this child’s traits
            return Object.entries(selectedOptions).every(([traitId, selectedValue]) =>
                childTraits.some(traitValue => traitValue.styleTraitId === traitId && traitValue.id === selectedValue),
            );
        });

        // Deep copy variantTraits to avoid mutation
        const tempVariantTraits =
            variantTraits?.map(trait => ({
                ...trait,
                traitValues: [] as { id: string; value: string; valueDisplay: string }[],
            })) ?? [];

        // build values of variantTraits dropdown depending on the available
        filteredChildren.forEach(({ product }) =>
            product.childTraitValues?.forEach(traitValue => {
                const trait = tempVariantTraits.find(t => t.id === traitValue.styleTraitId);
                if (trait && !trait.traitValues.some(tv => tv.id === traitValue.id)) {
                    trait.traitValues.push({
                        id: traitValue.id,
                        value: traitValue.value,
                        valueDisplay: traitValue.valueDisplay,
                    });
                }
            }),
        );
        return tempVariantTraits;
    }, [stableVariantChildContexts, selectedOptions, pagination.filteredChildren]);

    const searchOptions = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newQuery = event.target.value;
        setFilterQuery(newQuery);
    };

    const sortData = (sort: ProductOptionSorting): ProductOptionPagination => {
        const tempPagination = { ...pagination, sort };

        tempPagination.filteredChildren = sortingMethods[sort.type](
            tempPagination.filteredChildren,
            sort.asc,
            sort.data,
        );

        tempPagination.visibleChildren =
            pagination.page > 1 ? pagination.filteredChildren : pagination.filteredChildren.slice(0, pageSize);

        return tempPagination;
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (pagination.sort.data === sortField) {
            sorted = pagination.sort.asc ? "ascending" : "descending";
        }
        return sorted as SortOrderOptions;
    };

    const sortByTrait = (traitId: string) => {
        setPagination(sortData({ type: "traitId", asc: !pagination.sort.asc, data: traitId }));
        setPageChanged(new Date().getTime());
    };

    const loadPrices = () => {
        const productInfos: ProductInfo[] = [];
        pagination.visibleChildren?.forEach(childContext => {
            if (!childContext.productInfo.pricing) {
                productInfos.push(childContext.productInfo);
            }
        });

        if (productInfos.length === 0) {
            return;
        }

        loadRealTimePricing({
            productPriceParameters: productInfos,
            onSuccess: result => {
                result.realTimePricingResults?.forEach(pricing => {
                    const childContext = pagination.visibleChildren.find(
                        ({ productInfo }) => productInfo.productId === pricing.productId,
                    );
                    if (childContext) {
                        childContext.productInfo.pricing = pricing;
                        delete childContext.productInfo.failedToLoadPricing;
                    }
                });
                setPagination(cloneDeep(pagination));
            },
            onError: () => {
                pagination.visibleChildren.every(({ productInfo }) => {
                    productInfo.failedToLoadPricing = true;
                    delete productInfo.pricing;
                    return true;
                });
                setPagination(cloneDeep(pagination));
            },
        });

        showInventoryAvailability &&
            loadRealTimeInventory({
                productIds: productInfos.map(o => o.productId),
                ...(enableRealTimeBomFabricationCheck ? { properties: { CheckBomAvailability: "true" } } : {}),
                onSuccess: realTimeInventory => {
                    if (realTimeInventory) {
                        realTimeInventory.realTimeInventoryResults?.forEach((inventory: ProductInventoryDto) => {
                            const childContext = pagination.visibleChildren.find(
                                ({ productInfo }) => productInfo.productId === inventory.productId,
                            );
                            if (childContext) {
                                childContext.productInfo = {
                                    ...childContext.productInfo,
                                    inventory,
                                };
                            }
                        });
                        setPagination(cloneDeep(pagination));
                    }
                },
            });
    };

    const setVariantValues = (variantValues: []) => {
        productDetailsVariantOptionsContext.setVariantValues(variantValues);
    };

    const setVariantIds = (variantIds: []) => {
        productDetailsVariantOptionsContext.setVariantIds(variantIds);
    };

    const handleRowClick = (variantValues: [], variantIds: []) => {
        setVariantValues(variantValues);
        setVariantIds(variantIds);
    };

    const resetVariantselection = () => {
        if (variantTraits && variantTraits.length) {
            for (let i = 0; i < variantTraits.length; i++) {
                updateVariantSelection({
                    traitValueId: "",
                    variantTraitId: variantTraits[i].id,
                    productId: parentProductId,
                } as any);
            }
        }
    };

    const handleShowMoreOptions = () => {
        setPagination({
            ...pagination,
            page: 2,
            visibleChildren: pagination.filteredChildren,
        });
        setPageChanged(new Date().getTime());
    };

    // clear selection and do sorting
    const handleClearAll = () => {
        setSelectedOptions({});
        setFilterQuery("");
        setPagination(pagination => {
            const tempPagination = {
                ...pagination,
            };
            const items = sortingMethods[pagination.sort.type](
                variantChildContexts,
                pagination.sort.asc,
                pagination.sort.data,
            );

            tempPagination.filteredChildren = items;
            tempPagination.totalItemCount = tempPagination.filteredChildren.length;
            tempPagination.visibleChildren =
                pagination.page > 1
                    ? tempPagination.filteredChildren
                    : tempPagination.filteredChildren.slice(0, pageSize);
            tempPagination.oneImageAvailable = hasOneAvailableImage(tempPagination.visibleChildren);

            return tempPagination;
        });

        setPageChanged(new Date().getTime());
    };

    useEffect(() => {
        sortTraitValuesOfVisibleChildren(variantTraits ?? [], pagination.visibleChildren);
        loadPrices();
    }, [pageChanged]);

    if (isVariantOptionsLoading) {
        return <Skeleton height="280px" />;
    }

    if (!variantTraits || variantTraits.length === 0 || !variantChildContexts || !variantChildContexts.length) {
        return null;
    }
    return (
        <Accordion headingLevel={2} id={anchorId}>
            <ManagedAccordionSection
                title={
                    isTabletScreen
                        ? translate("Product Options")
                        : translate("{0} Product Options", pagination.filteredChildren?.length?.toString())
                }
                initialExpanded
                {...styles.accordion}
            >
                <StyledWrapper {...styles.filtersWrapper}>
                    <StyledWrapper {...styles.searchWrapper}>
                        <TextField
                            {...styles.searchInput}
                            onChange={searchOptions}
                            placeholder={translate("Search By EIS Part #")}
                            autoComplete="off"
                            label={translate("Filter Results")}
                            data-test-selector="productOptionsInputTextField"
                            value={filterQuery}
                            iconProps={{
                                src: Search,
                            }}
                        />
                    </StyledWrapper>
                    {filteredVariantTraits.map(variantTrait => {
                        return (
                            <StyledWrapper key={`${variantTrait.id}_wrapper`}>
                                <Select
                                    key={variantTrait.id}
                                    label={variantTrait.nameDisplay}
                                    value={selectedOptions[variantTrait.id] ?? ""}
                                    onChange={event => {
                                        handleChange(variantTrait.id, event.currentTarget.value);
                                    }}
                                    data-test-selector={`styleSelect_${variantTrait.name}`}
                                >
                                    <option value="" key={variantTrait.id}>
                                        {`${translate("Select")} ${variantTrait.nameDisplay}`}
                                    </option>
                                    {variantTrait.traitValues?.map(traitValue => (
                                        <option value={`${traitValue.id}`} key={`${traitValue.id}`}>
                                            {traitValue.valueDisplay}
                                        </option>
                                    ))}
                                </Select>
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
                {pagination.filteredChildren.length !== variantChildContexts.length && (
                    <Link onClick={handleClearAll} {...styles.clearAll}>
                        {translate("Clear All Selections")}
                    </Link>
                )}
                <StyledWrapper {...styles.divider} />
                <StyledWrapper {...styles.tableWrapper}>
                    <DataTable
                        useResponsiveViewForTablet={true}
                        css={css`
                            position: relative;
                        `}
                    >
                        <DataTableHead>
                            <DataTableHeader {...styles.tableHeaderCell} />
                            {variantTraits?.map(trait => (
                                <DataTableHeader
                                    key={trait.id}
                                    onSortClick={() => sortByTrait(trait.id)}
                                    sorted={sorted(trait.id)}
                                    {...styles.tableHeaderCell}
                                >
                                    {trait.nameDisplay}
                                </DataTableHeader>
                            ))}
                            <DataTableHeader {...styles.tableHeaderCell}>{translate("Stock Status")}</DataTableHeader>
                            <DataTableHeader {...styles.tableHeaderCell} />
                        </DataTableHead>
                        <DataTableBody>
                            {pagination.visibleChildren.map(({ product, productInfo }) => {
                                const hasMultipleSalesQty =
                                    Number(
                                        product.properties?.multipleSaleQty ?? product.detail?.multipleSaleQty ?? 0,
                                    ) > 1;

                                const hasMinOrderQty =
                                    Number(product.properties?.minimumOrderQty ?? product.minimumOrderQty ?? 0) > 1;
                                return (
                                    <ProductOptionContext key={product.id} product={product} productInfo={productInfo}>
                                        <DataTableRow
                                            {...(selectedVariantId === product.id
                                                ? styles.selectedTableRow
                                                : styles.tableRow)}
                                            onClick={() => {
                                                const variantValues = [] as any;
                                                const variantIds = [] as any;

                                                variantTraits?.map((value: any) => {
                                                    return product.childTraitValues?.forEach(variant => {
                                                        if (variant.styleTraitId === value.id) {
                                                            variantValues.push(variant.id);
                                                            variantIds.push(value.id);
                                                        }
                                                    });
                                                });

                                                // Reset the selection first to avoid issues when some styleTrait options have no corresponding child
                                                resetVariantselection();
                                                handleRowClick(variantValues, variantIds);
                                            }}
                                        >
                                            <DataTableCell {...styles.imageCell}>
                                                <LazyImage
                                                    {...styles.image}
                                                    src={product.smallImagePath}
                                                    altText={product.imageAltText}
                                                />
                                            </DataTableCell>
                                            {variantTraits?.map(value => (
                                                <DataTableCell {...{ header: value.nameDisplay }} key={value.id}>
                                                    {
                                                        product.childTraitValues?.find(
                                                            variant => variant.styleTraitId === value.id,
                                                        )?.valueDisplay
                                                    }
                                                </DataTableCell>
                                            ))}
                                            <DataTableCell {...{ header: "Stock Status" }}>
                                                {showInventoryAvailability && (
                                                    <ProductContextAvailability
                                                        extendedStyles={styles.availabilityExtendedStyles}
                                                    />
                                                )}
                                            </DataTableCell>
                                            <DataTableCell {...styles.actionCellWrapper}>
                                                <StyledWrapper {...styles.actionsCell}>
                                                    <ProductContext.Consumer>
                                                        {value => (
                                                            <ProductPrice
                                                                showLabel={false}
                                                                product={value}
                                                                extendedStyles={styles.priceExtendedStyles}
                                                            />
                                                        )}
                                                    </ProductContext.Consumer>
                                                    <StyledWrapper {...styles.productActionsWrapper}>
                                                        <ProductQuantityOrdered
                                                            labelOverride=""
                                                            id={`${product.id}-qty`}
                                                        />
                                                        <ProductAddToCartButton
                                                            iconOnly={true}
                                                            data-test-selector={`actionsAddToCart${product.id}`}
                                                            sizeVariant="small"
                                                        />
                                                        {(hasMinOrderQty || hasMultipleSalesQty) && (
                                                            <Tooltip
                                                                passAsChildren={true}
                                                                trigger="hover"
                                                                triggerComponent={
                                                                    <Icon {...styles.tooltipIcon} src={LinkInfo} />
                                                                }
                                                                {...styles.tooltip}
                                                            >
                                                                <StyledWrapper {...styles.tooltipTextWrapper}>
                                                                    <SmallHeadingAndText
                                                                        extendedStyles={styles.headingAndTextStyles}
                                                                        heading={translate("Qty Increment")}
                                                                        text={
                                                                            product.properties?.multipleSaleQty ||
                                                                            product.detail?.multipleSaleQty ||
                                                                            1
                                                                        }
                                                                    />

                                                                    <SmallHeadingAndText
                                                                        extendedStyles={styles.headingAndTextStyles}
                                                                        heading={translate("Min Qty")}
                                                                        text={
                                                                            product.properties?.minimumOrderQty ||
                                                                            product.minimumOrderQty ||
                                                                            1
                                                                        }
                                                                    />
                                                                </StyledWrapper>
                                                            </Tooltip>
                                                        )}
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            </DataTableCell>
                                        </DataTableRow>
                                    </ProductOptionContext>
                                );
                            })}
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>
                {pagination.visibleChildren.length === 0 && (
                    <Notify messageType="info" body={siteMessage("PDP_Variant_Product_Options_No_Results")} />
                )}
                {pagination.totalItemCount > pageSize && pagination.visibleChildren.length === pageSize && (
                    <StyledWrapper {...styles.buttonWrapper}>
                        <Button onClick={handleShowMoreOptions}>
                            {translate("Show All {0} Options", pagination.totalItemCount.toString())}
                        </Button>
                    </StyledWrapper>
                )}
            </ManagedAccordionSection>
        </Accordion>
    );
};

const widgetModule: WidgetModule = {
    component: withParentProductId(connect(mapStateToProps, mapDispatchToProps)(ProductOptions)),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
